import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Users, RefreshCw, AlertCircle, User, Calendar, ArrowDownRight, List, LayoutGrid, ChevronUp, ChevronDown, Eye } from 'lucide-react';
import { useAuth } from '../../auth';
import { useAPIStorage } from '../../hooks/useAPIStorage';
import { EmployeeDetail } from '../HR/EmployeeDetail';
import { TeamMemberCalendar } from './TeamMemberCalendar';
import { ManagerExportPanel } from './ManagerExportPanel';
import { DateRangePicker } from '../common/DateRangePicker';
import { EmployeeViewTabs, TAB_BUTTON_ID, TAB_PANEL_ID } from '../common/EmployeeViewTabs';
import type { DayEntry, UserEntry, TeamMemberEntry, UserTeamStats, CompanyVacationRule, WageType } from '../../types';
import { EMPLOYMENT_TYPE_LABELS } from '../../types';
import { getCompanyVacationDates, mergeCompanyVacationIntoEntries } from '../../utils/companyVacation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableFooter, TableRow, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent } from '@/components/ui/dialog';

type ViewMode = 'list' | 'grouped';
type SortKey = 'name' | 'department' | 'totalHours' | 'workDays' | 'sickDays' | 'vacationEntitlement' | 'vacationDaysTaken' | 'vacationDaysRemaining';
type SortDir = 'asc' | 'desc';

function getMonthStart(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

function getMonthEnd(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const lastDay = new Date(y, m, 0).getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

function formatDE(value: number | null | undefined, decimals: number = 1): string {
  return (value ?? 0).toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function ManagerDashboard() {
  const { isManager } = useAuth();
  const { getMyTeam, getUserEntries, getTeamStats, getCompanyVacationRule } = useAPIStorage();

  const [teamMembers, setTeamMembers] = useState<TeamMemberEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // View-Toggle
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Zeitraum-Picker
  const [dateFrom, setDateFrom] = useState(getMonthStart);
  const [dateTo, setDateTo] = useState(getMonthEnd);

  // Team-Stats
  const [teamStats, setTeamStats] = useState<UserTeamStats[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Sortierung
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // States fuer Kalender-Ansicht
  const [calendarEntries, setCalendarEntries] = useState<Record<string, DayEntry>>({});
  const [previousYearEntries, setPreviousYearEntries] = useState<Record<string, DayEntry>>({});
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);
  const [calendarDataLoaded, setCalendarDataLoaded] = useState(false);

  // Einheitlicher State fuer ausgewaehlten Mitarbeiter + Tab
  const [selectedMember, setSelectedMember] = useState<UserEntry | null>(null);
  const [employeeViewTab, setEmployeeViewTab] = useState<'details' | 'calendar'>('details');

  const currentYear = new Date().getFullYear();

  // Betriebsferien-Regel cachen (einmal laden bei Kalender-Öffnung)
  const companyVacationRuleRef = useRef<CompanyVacationRule | null>(null);

  /** Betriebsferien mit dem WageType des betrachteten Mitarbeiters mergen */
  const applyCompanyVacationForUser = useCallback((
    entries: Record<string, DayEntry>,
    year: number,
    wageType: WageType | null | undefined,
  ): Record<string, DayEntry> => {
    const rule = companyVacationRuleRef.current;
    if (!rule) return entries;
    const dates = getCompanyVacationDates(rule, year);
    return mergeCompanyVacationIntoEntries(entries, dates, wageType ?? null);
  }, []);

  // Ref fuer popstate-Handler (vermeidet stale closures)
  const selectedMemberRef = useRef(selectedMember);
  selectedMemberRef.current = selectedMember;

  // Gibt es indirekte Mitarbeiter?
  const hasIndirect = useMemo(() => teamMembers.some((m) => m.depth > 1), [teamMembers]);

  // Gruppierung nach Ebenen (nur wenn es indirekte gibt)
  const depthGroups = useMemo(() => {
    if (!hasIndirect) return [];

    const groups = new Map<number, TeamMemberEntry[]>();
    for (const m of teamMembers) {
      const arr = groups.get(m.depth) || [];
      arr.push(m);
      groups.set(m.depth, arr);
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([depth, members]) => {
        const subGroups = new Map<string, TeamMemberEntry[]>();
        for (const m of members) {
          const key = m.viaManagerName || '__direct__';
          const arr = subGroups.get(key) || [];
          arr.push(m);
          subGroups.set(key, arr);
        }

        for (const arr of subGroups.values()) {
          arr.sort((a, b) =>
            (a.lastName || '').localeCompare(b.lastName || '', 'de')
            || (a.firstName || '').localeCompare(b.firstName || '', 'de')
          );
        }

        return {
          depth,
          members,
          subGroups: Array.from(subGroups.entries()).sort(([a], [b]) => a.localeCompare(b, 'de')),
        };
      });
  }, [teamMembers, hasIndirect]);

  // Sortierte Stats
  const sortedStats = useMemo(() => {
    const sorted = [...teamStats];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name': cmp = a.userName.localeCompare(b.userName, 'de'); break;
        case 'department': cmp = (a.department || '').localeCompare(b.department || '', 'de'); break;
        case 'totalHours': cmp = a.totalHours - b.totalHours; break;
        case 'workDays': cmp = a.workDays - b.workDays; break;
        case 'sickDays': cmp = a.sickDays - b.sickDays; break;
        case 'vacationEntitlement': cmp = a.vacationEntitlement - b.vacationEntitlement; break;
        case 'vacationDaysTaken': cmp = a.vacationDaysTaken - b.vacationDaysTaken; break;
        case 'vacationDaysRemaining': cmp = a.vacationDaysRemaining - b.vacationDaysRemaining; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [teamStats, sortKey, sortDir]);

  // Summen
  const totals = useMemo(() => {
    return teamStats.reduce(
      (acc, s) => ({
        totalHours: acc.totalHours + s.totalHours,
        workDays: acc.workDays + s.workDays,
        sickDays: acc.sickDays + s.sickDays,
        vacationDaysTaken: acc.vacationDaysTaken + s.vacationDaysTaken,
      }),
      { totalHours: 0, workDays: 0, sickDays: 0, vacationDaysTaken: 0 }
    );
  }, [teamStats]);

  // Browser-Zurueck: Sub-Views schliessen
  useEffect(() => {
    const handlePopState = () => {
      if (selectedMemberRef.current) {
        setSelectedMember(null);
        setCalendarDataLoaded(false);
        setCalendarYear(currentYear);
        setEmployeeViewTab('details');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentYear]);

  const loadTeamMembers = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const members = await getMyTeam();
      setTeamMembers(members);
    } catch (e) {
      console.error('ManagerDashboard: Fehler beim Laden:', e);
      setError(e instanceof Error ? e.message : 'Fehler beim Laden der Team-Mitglieder');
    } finally {
      setIsLoading(false);
    }
  }, [getMyTeam]);

  useEffect(() => {
    void loadTeamMembers();
  }, [loadTeamMembers]);

  // Team-Stats laden wenn Listenansicht aktiv + Zeitraum sich ändert
  const loadTeamStats = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setIsLoadingStats(true);
    try {
      const result = await getTeamStats(dateFrom, dateTo);
      setTeamStats(result.stats);
    } catch (e) {
      console.error('Fehler beim Laden der Team-Statistiken:', e);
      setError(e instanceof Error ? e.message : 'Fehler beim Laden der Statistiken');
    } finally {
      setIsLoadingStats(false);
    }
  }, [getTeamStats, dateFrom, dateTo]);

  // Debounce: Stats laden bei Zeitraumänderung
  useEffect(() => {
    if (viewMode !== 'list') return;
    const timer = setTimeout(loadTeamStats, 300);
    return () => clearTimeout(timer);
  }, [viewMode, loadTeamStats]);

  // Zeitraum-Änderung
  const handleDateRangeChange = useCallback((from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
  }, []);

  // Sortierung toggle
  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return key;
      }
      setSortDir('asc');
      return key;
    });
  }, []);

  // Kalenderdaten laden (ohne History-Manipulation)
  const calendarLoadIdRef = useRef(0);
  const loadCalendarData = useCallback(async (userEntry: UserEntry) => {
    const loadId = ++calendarLoadIdRef.current;
    setIsLoadingCalendar(true);
    try {
      setCalendarYear(currentYear);
      const [currentEntries, previousEntries, vacationRule] = await Promise.all([
        getUserEntries(userEntry.id, currentYear),
        getUserEntries(userEntry.id, currentYear - 1),
        getCompanyVacationRule(),
      ]);
      // Veraltete Ergebnisse ignorieren (schneller Member-Wechsel)
      if (calendarLoadIdRef.current !== loadId) return;
      companyVacationRuleRef.current = vacationRule;
      const mergedEntries = applyCompanyVacationForUser(currentEntries || {}, currentYear, userEntry.wageType);
      setCalendarEntries(mergedEntries);
      setPreviousYearEntries(previousEntries || {});
      setCalendarDataLoaded(true);
    } catch (e) {
      if (calendarLoadIdRef.current !== loadId) return;
      console.error('Fehler beim Laden der Kalenderdaten:', e);
      setError('Fehler beim Laden der Kalenderdaten');
    } finally {
      if (calendarLoadIdRef.current === loadId) {
        setIsLoadingCalendar(false);
      }
    }
  }, [currentYear, getUserEntries, getCompanyVacationRule, applyCompanyVacationForUser]);

  // Handler fuer Kalender oeffnen (aus Liste/Karte)
  const handleOpenCalendar = async (userEntry: UserEntry) => {
    setSelectedMember(userEntry);
    setEmployeeViewTab('calendar');
    await loadCalendarData(userEntry);
    window.history.pushState({ tab: 'manager', view: 'employee', userId: userEntry.id }, '');
  };

  // Handler fuer Jahreswechsel im Kalender
  const handleYearChange = async (newYear: number) => {
    if (!selectedMember) return;
    setIsLoadingCalendar(true);
    try {
      setCalendarYear(newYear);
      const [currentEntries, previousEntries] = await Promise.all([
        getUserEntries(selectedMember.id, newYear),
        getUserEntries(selectedMember.id, newYear - 1),
      ]);
      const mergedEntries = applyCompanyVacationForUser(currentEntries || {}, newYear, selectedMember.wageType);
      setCalendarEntries(mergedEntries);
      setPreviousYearEntries(previousEntries || {});
    } catch (e) {
      console.error('Fehler beim Laden der Kalenderdaten:', e);
      setError('Fehler beim Laden der Kalenderdaten');
    } finally {
      setIsLoadingCalendar(false);
    }
  };

  // Zurueck zur Team-Uebersicht
  const handleBackToList = () => {
    window.history.back();
  };

  const handleOpenDetail = (userEntry: UserEntry) => {
    setSelectedMember(userEntry);
    setEmployeeViewTab('details');
    setCalendarDataLoaded(false);
    window.history.pushState({ tab: 'manager', view: 'employee', userId: userEntry.id }, '');
  };

  // Tab-Wechsel zwischen Details und Kalender
  const handleEmployeeTabChange = async (tab: 'details' | 'calendar') => {
    if (tab === 'calendar' && !calendarDataLoaded && selectedMember) {
      await loadCalendarData(selectedMember);
    }
    setEmployeeViewTab(tab);
  };

  const statToUserEntry = useCallback((stat: UserTeamStats): UserEntry => {
    const member = teamMembers.find((m) => m.id === stat.userId);
    if (member) return member;
    return {
      id: stat.userId,
      name: stat.userName,
      email: '',
      department: stat.department as UserEntry['department'],
      lastActive: '',
    };
  }, [teamMembers]);

  const handleOpenDetailByStats = (stat: UserTeamStats) => {
    handleOpenDetail(statToUserEntry(stat));
  };

  const handleOpenCalendarByStats = (stat: UserTeamStats) => {
    void handleOpenCalendar(statToUserEntry(stat));
  };

  const handleCalendarRefresh = async () => {
    if (selectedMember) {
      const entries = await getUserEntries(selectedMember.id, calendarYear);
      const mergedEntries = applyCompanyVacationForUser(entries || {}, calendarYear, selectedMember.wageType);
      setCalendarEntries(mergedEntries);
    }
  };

  if (!isManager) {
    return (
      <div className="border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 dark:text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
          Kein Zugriff
        </h2>
        <p className="text-red-600 dark:text-red-300">
          Diese Seite ist nur für Abteilungsleiter zugänglich.
        </p>
      </div>
    );
  }

  // Mitarbeiter ausgewaehlt: Tab-Ansicht (Details / Kalender)
  if (selectedMember) {
    const tabBar = (
      <EmployeeViewTabs
        activeTab={employeeViewTab}
        onTabChange={handleEmployeeTabChange}
      />
    );

    return (
      <>
        <div
          id={TAB_PANEL_ID.details}
          role="tabpanel"
          aria-labelledby={TAB_BUTTON_ID.details}
          className={employeeViewTab !== 'details' ? 'hidden' : ''}
        >
          <EmployeeDetail
            user={selectedMember}
            onBack={handleBackToList}
            viewMode="manager"
            tabBar={tabBar}
          />
        </div>
        {calendarDataLoaded && (
          <div
            id={TAB_PANEL_ID.calendar}
            role="tabpanel"
            aria-labelledby={TAB_BUTTON_ID.calendar}
            className={employeeViewTab !== 'calendar' ? 'hidden' : ''}
          >
            <TeamMemberCalendar
              userId={selectedMember.id}
              userName={selectedMember.name}
              year={calendarYear}
              entries={calendarEntries}
              previousYearEntries={previousYearEntries}
              onBack={handleBackToList}
              onRefresh={handleCalendarRefresh}
              onYearChange={handleYearChange}
              viewedUserWageType={selectedMember.wageType}
              tabBar={tabBar}
              employmentStart={selectedMember.employmentStart}
              employmentEnd={selectedMember.employmentEnd}
            />
          </div>
        )}
        {/* Loading Indicator fuer Kalender */}
        <Dialog open={isLoadingCalendar}>
          <DialogContent className="max-w-xs [&>button.absolute]:hidden" onInteractOutside={(e) => e.preventDefault()}>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-gray-900 dark:border-gray-100 border-t-transparent rounded-full animate-spin" />
              <span className="text-gray-700 dark:text-gray-300">Lade Kalenderdaten...</span>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <Users className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
                  Team-Dashboard
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Übersicht Ihrer zugeordneten Team-Mitglieder
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* View-Toggle */}
              <div className="inline-flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors
                    ${viewMode === 'list'
                      ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden sm:inline">Liste</span>
                </button>
                <button
                  onClick={() => setViewMode('grouped')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm transition-colors
                    ${viewMode === 'grouped'
                      ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <LayoutGrid className="w-4 h-4" />
                  <span className="hidden sm:inline">Ebenen</span>
                </button>
              </div>
              <Button
                onClick={() => { void loadTeamMembers(); if (viewMode === 'list') void loadTeamStats(); }}
                disabled={isLoading}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Aktualisieren</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stundenliste exportieren (Manager-Export, ohne Lohndaten) */}
      {teamMembers.length > 0 && (
        <ManagerExportPanel teamMembers={teamMembers} />
      )}

      {/* Zeitraum-Picker (nur in Listenansicht) */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="p-4">
            <DateRangePicker from={dateFrom} to={dateTo} onChange={handleDateRangeChange} />
          </CardContent>
        </Card>
      )}

      {error && (
        <div className="border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Listenansicht */}
      {viewMode === 'list' && (
        <Card className="overflow-hidden">
          {isLoadingStats ? (
            <div className="p-8 flex items-center justify-center gap-3 text-gray-500 dark:text-gray-400">
              <div className="w-5 h-5 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
              <span>Lade Statistiken...</span>
            </div>
          ) : teamStats.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              Keine Daten für den gewählten Zeitraum
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <SortHeader label="Name" sortKey="name" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="text-left pl-4" />
                  <SortHeader label="Abteilung" sortKey="department" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="text-left" />
                  <SortHeader label="Stunden" sortKey="totalHours" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                  <SortHeader label="Arbeitstage" sortKey="workDays" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                  <SortHeader label="Krankheit" sortKey="sickDays" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                  <SortHeader label="Anspruch" sortKey="vacationEntitlement" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                  <SortHeader label="Genommen" sortKey="vacationDaysTaken" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                  <SortHeader label="Rest" sortKey="vacationDaysRemaining" currentKey={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                  <th className="px-3 py-2.5 text-xs font-medium text-muted-foreground uppercase tracking-wider text-center pr-4">
                    Aktionen
                  </th>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedStats.map((stat) => (
                  <TableRow key={stat.userId} className="group/row">
                    <TableCell className="px-3 py-2.5 pl-4">
                      <button
                        onClick={() => handleOpenDetailByStats(stat)}
                        className="text-sm font-medium text-foreground/80 hover:underline text-left"
                      >
                        {stat.userName}
                      </button>
                    </TableCell>
                    <TableCell className="px-3 py-2.5">
                      {stat.department ? (
                        <Badge variant="secondary" className="px-1.5 py-0.5 text-[10px] font-medium rounded">
                          {stat.department}
                        </Badge>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-right tabular-nums">
                      {formatDE(stat.totalHours)}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-right tabular-nums">
                      {stat.workDays}
                    </TableCell>
                    <TableCell className={`px-3 py-2.5 text-right tabular-nums ${stat.sickDays > 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                      {stat.sickDays}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-right tabular-nums">
                      {formatDE(stat.vacationEntitlement)}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-right tabular-nums">
                      {formatDE(stat.vacationDaysTaken)}
                    </TableCell>
                    <TableCell className={`px-3 py-2.5 text-right tabular-nums font-medium ${stat.vacationDaysRemaining < 0 ? 'text-red-600 dark:text-red-400' : stat.vacationDaysRemaining > 0 ? 'text-green-600 dark:text-green-400' : ''}`}>
                      {formatDE(stat.vacationDaysRemaining)}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 pr-4 text-center">
                      <div className="flex items-center justify-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
                        <Button
                          onClick={() => handleOpenCalendarByStats(stat)}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                          title="Kalender anzeigen"
                        >
                          <Calendar className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleOpenDetailByStats(stat)}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:bg-muted"
                          title="Details anzeigen"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="px-3 py-2.5 pl-4 font-semibold">Gesamt</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="px-3 py-2.5 text-right tabular-nums font-semibold">{formatDE(totals.totalHours)}</TableCell>
                  <TableCell className="px-3 py-2.5 text-right tabular-nums font-semibold">{totals.workDays}</TableCell>
                  <TableCell className="px-3 py-2.5 text-right tabular-nums font-semibold">{totals.sickDays}</TableCell>
                  <TableCell className="px-3 py-2.5 text-right text-muted-foreground">—</TableCell>
                  <TableCell className="px-3 py-2.5 text-right tabular-nums font-semibold">{formatDE(totals.vacationDaysTaken)}</TableCell>
                  <TableCell className="px-3 py-2.5 text-right text-muted-foreground">—</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          )}
          {teamStats.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-white/10 text-xs text-gray-500 dark:text-gray-400">
              <span className="tabular-nums">{teamStats.length}</span>{' '}
              {teamStats.length === 1 ? 'Teammitglied' : 'Teammitglieder'}
            </div>
          )}
        </Card>
      )}

      {/* Ebenen-Ansicht (gruppiert) */}
      {viewMode === 'grouped' && (
        !hasIndirect ? (
          <FlatTeamView
            members={teamMembers}
            isLoading={isLoading}
            onSelectUser={handleOpenDetail}
            onCalendarEdit={handleOpenCalendar}
          />
        ) : (
          <GroupedTeamView
            groups={depthGroups}
            isLoading={isLoading}
            onSelectUser={(member) => handleOpenDetail(member)}
            onCalendarEdit={handleOpenCalendar}
          />
        )
      )}

      {/* Hinweis */}
      {teamMembers.length === 0 && !isLoading && !error && (
        <div className="border border-amber-300/60 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-900/10 rounded-lg p-4">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            <strong>Hinweis:</strong> Dir sind noch keine Mitarbeiter zugewiesen.
          </p>
        </div>
      )}

      {/* Loading Indicator fuer Kalender */}
      <Dialog open={isLoadingCalendar}>
        <DialogContent className="max-w-xs [&>button.absolute]:hidden" onInteractOutside={(e) => e.preventDefault()}>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-gray-900 dark:border-gray-100 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-700 dark:text-gray-300">Lade Kalenderdaten...</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==========================================
// Sortier-Header
// ==========================================

function SortHeader({ label, sortKey, currentKey, dir, onSort, className = '' }: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const isActive = currentKey === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`px-3 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer
                  hover:text-gray-700 dark:hover:text-gray-200 select-none whitespace-nowrap ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          dir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </span>
    </th>
  );
}

// ==========================================
// Flache Team-Ansicht (nur direkte Mitarbeiter als Karten)
// ==========================================

function FlatTeamView({ members, isLoading, onSelectUser, onCalendarEdit }: {
  members: TeamMemberEntry[];
  isLoading: boolean;
  onSelectUser: (member: TeamMemberEntry) => void;
  onCalendarEdit: (member: TeamMemberEntry) => void;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-3 text-gray-500 dark:text-gray-400">
            <div className="w-5 h-5 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
            <span>Lade Mitarbeiterliste...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {members.map((member) => (
            <TeamMemberCard
              key={member.id}
              member={member}
              onSelect={() => onSelectUser(member)}
              onCalendar={() => onCalendarEdit(member)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ==========================================
// Gruppierte Team-Ansicht mit Sektions-Headern
// ==========================================

interface DepthGroup {
  depth: number;
  members: TeamMemberEntry[];
  subGroups: [string, TeamMemberEntry[]][];
}

function GroupedTeamView({
  groups,
  isLoading,
  onSelectUser,
  onCalendarEdit,
}: {
  groups: DepthGroup[];
  isLoading: boolean;
  onSelectUser: (member: TeamMemberEntry) => void;
  onCalendarEdit: (member: TeamMemberEntry) => void;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-3 text-gray-500 dark:text-gray-400">
            <div className="w-5 h-5 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
            <span>Lade Mitarbeiterliste...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map(({ depth, members, subGroups }) => (
        <Card key={depth} className="overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-900">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              {depth === 1 ? 'Direkte Mitarbeiter' : `Ebene ${depth}`}
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400 tabular-nums">
                ({members.length})
              </span>
            </h2>
          </div>

          <div className="p-4 space-y-4">
            {depth === 1 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {members.map((member) => (
                  <TeamMemberCard
                    key={member.id}
                    member={member}
                    onSelect={() => onSelectUser(member)}
                    onCalendar={() => onCalendarEdit(member)}
                  />
                ))}
              </div>
            ) : (
              subGroups.map(([viaName, groupMembers]) => (
                <div key={viaName}>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    über {viaName}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {groupMembers.map((member) => (
                      <TeamMemberCard
                        key={member.id}
                        member={member}
                        onSelect={() => onSelectUser(member)}
                        onCalendar={() => onCalendarEdit(member)}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ==========================================
// Einzelne Team-Mitglied-Karte
// ==========================================

function TeamMemberCard({
  member,
  onSelect,
  onCalendar,
}: {
  member: TeamMemberEntry;
  onSelect: () => void;
  onCalendar: () => void;
}) {
  const displayName = member.firstName && member.lastName
    ? `${member.firstName} ${member.lastName}`
    : member.name;

  const employmentLabel = member.employmentType
    ? EMPLOYMENT_TYPE_LABELS[member.employmentType]
    : null;

  return (
    <div
      onClick={onSelect}
      className="relative group rounded-lg border p-4 cursor-pointer transition-all
                 hover:border-gray-300 dark:hover:border-gray-600
                 border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900"
    >
      <div className="flex flex-col items-center text-center">
        <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full
                        flex items-center justify-center mb-3">
          <User className="w-6 h-6 text-gray-500 dark:text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate w-full">
          {displayName}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate w-full mt-0.5">
          {member.email}
        </p>

        <div className="flex items-center gap-1.5 mt-2 flex-wrap justify-center">
          {member.department && (
            <Badge variant="secondary" className="px-1.5 py-0.5 text-[10px] font-medium rounded">
              {member.department}
            </Badge>
          )}
          {employmentLabel && (
            <Badge variant="secondary" className="px-1.5 py-0.5 text-[10px] font-medium rounded border-teal-200 bg-teal-100 text-teal-700 dark:border-teal-800 dark:bg-teal-900/40 dark:text-teal-300">
              {employmentLabel}
            </Badge>
          )}
          {member.isManager && (
            <Badge variant="info" className="px-1.5 py-0.5 text-[10px] rounded">
              Manager
            </Badge>
          )}
        </div>

        {member.depth > 1 && member.viaManagerName && (
          <div className="flex items-center gap-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <ArrowDownRight className="w-3 h-3" />
            <span>über {member.viaManagerName}</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-0.5 mt-3 pt-3 border-t border-gray-100 dark:border-white/10">
        <Button
          onClick={(e) => { e.stopPropagation(); onCalendar(); }}
          variant="ghost"
          size="icon"
          className="text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30"
          title="Kalender anzeigen"
        >
          <Calendar className="w-4.5 h-4.5" />
        </Button>
      </div>
    </div>
  );
}
